import React, {useEffect, useRef, useState} from "react";
import {Button, DatePicker, Form, Radio, Row, Select} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {getBookingIncrement, roundToBookingIncrement} from "../helpers/booking";
import {buildLocationDescription, getAllLocations} from "../helpers/location";
import {createDeployment} from "../helpers/deployment";
import {messageType, showMessages} from "../helpers/message";

import {formInCardLayout, formItemFullWidth} from "../assets/styles";
import './DeploymentCreateForm.less';
import {dateTimeFormat, timeDisplayFormat, timestampFormat} from "../lib/time";

const DeploymentCreateForm = props => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const [locationData, setLocationData] = useState([]);
    const [locationID, setLocationID] = useState(null);
    const [locationLoading, setLocationLoading] = useState(true);

    const deployableID = props.carID;
    const deployableType = 'BookCar';

    const [form] = Form.useForm();

    //region Set Booking Increment
    const bookingIncrement = getBookingIncrement();
    const bookingIncrementMinutes = Number(bookingIncrement) / 60;
    const timePickerProps = {
        showTime: {
            minuteStep: bookingIncrementMinutes,
            use12Hours: true,
            format: timeDisplayFormat,
        },
        format: dateTimeFormat,
    };

    //endregion
    //region Load Location

    useEffect(() => {
        const populateLocationSelect = async () => {

            const abortSignal = abortSignalRef.current;

            try {
                const response = await getAllLocations(1, 999999, 'name', abortSignal);

                if (response) {
                    setLocationData(response.data);
                    setLocationLoading(false);
                }

            } catch (error) {
                showMessages(error.message, messageType.error);
                setLocationLoading(false);
                if(error.status_code === 404) window.location.replace('/404');
            }
        };

        populateLocationSelect();

    }, []);

    //endregion
    //region Cleanup

    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    //endregion
    //region Form functions

    const [startedAt, setStartedAt] = useState(null);
    const [endedAt, setEndedAt] = useState(null);
    const [endTimeEnabled, setEndTimeEnabled] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const onMoveTypeChange = e => {
        // When permanent type is selected, disable end time field and null-ify the end time value
        if (e.target.value === 'permanent') {
            setEndTimeEnabled(false);
            setEndedAt(null);

            form.resetFields(['End Date & Time']);
        } else if (e.target.value === 'temporary') {    // Re-enable end time when temporary type is selected
            setEndTimeEnabled(true);
        }
    }

    const onLocationChange = (value) => {
        setLocationID(value);
    };

    const onStartTimeChange = (value) => {
        value = value ? roundToBookingIncrement(value,'up') : null;

        const date = value ? value.startOf('minute').format(timestampFormat) : null;

        form.setFieldsValue({
            "Start Date & Time": value
        });
        setStartedAt(date);
    };

    const onEndTimeChange = (value) => {
        value = value ? roundToBookingIncrement(value,'up') : null;

        const date = value ? value.startOf('minute').format(timestampFormat) : null;

        form.setFieldsValue({
            "End Date & Time": value
        });
        setEndedAt(date);
    };

    const handleFinish = async (values) => {
        if (endedAt !== null && !isStartedAtBeforeEndedAt()) {
            showMessages('Please select an End Date & Time that is after the Start Date & Time!', messageType.error);
            return;
        }

        const abortSignal = abortSignalRef.current;

        try {
            setFormSubmitted(true);

            const response = await createDeployment({
                deployableID,
                deployableType,
                startedAt,
                endedAt,
                locationID,
            }, abortSignal);

            if (response) {
                setFormSubmitted(false);
                showMessages('Vehicle (and bookings) have successfully been moved in the system!', messageType.success);
            }

        } catch (error) {
            showMessages(error.message, messageType.error);
            setFormSubmitted(false);
            if(error.status_code === 404) window.location.replace('/404');
        }
    };

    //endregion
    //region Helpers

    const isStartedAtBeforeEndedAt = () => {
        const startedAtObj = dayjs(startedAt).tz();
        const endedAtObj = dayjs(endedAt).tz();

        if (startedAtObj.isBefore(endedAtObj)) {
            return true;
        }

        return false;
    };

    //endregion

    return (
        <Form
            {...formInCardLayout}
            form={form}
            name="deployment-create"
            onFinish={handleFinish}
            initialValues={{
                'Move Type': 'permanent',
            }}
            style={{width: '100%'}}
        >
            <Form.Item
                name="Move Type"
                label="Move Type"
                rules={[{required: true}]}
            >
                <Radio.Group
                    name="moveType"
                    onChange={onMoveTypeChange}
                >
                    <Radio value="permanent" className="radio-button">Permanent Move</Radio>
                    <Radio value="temporary" className="radio-button">Temporary Move</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item
                name="Location"
                label="Location"
                rules={[{required: true}]}
            >
                <Select
                    showSearch
                    placeholder='Select or search'
                    loading={locationLoading}
                    onChange={onLocationChange}
                    filterOption={(input, option) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    dropdownMatchSelectWidth={false}
                    className="deployment-location-select"
                >
                    {locationData.map((location) => (
                        <Select.Option key={location.id} value={location.id}>
                            {buildLocationDescription(location.name, location.description)}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name="Start Date & Time"
                label="Start Date & Time"
                rules={[{type: 'object', required: true}]}
            >
                <DatePicker
                    {...timePickerProps}
                    placeholder=""
                    onChange={onStartTimeChange}
                    style={{...formItemFullWidth}}
                />
            </Form.Item>
            <Form.Item
                name="End Date & Time"
                label="End Date & Time"
                rules={[{type: 'object', required: endTimeEnabled}]}
            >
                <DatePicker
                    {...timePickerProps}
                    placeholder=""
                    disabled={!endTimeEnabled}
                    onChange={onEndTimeChange}
                    style={{...formItemFullWidth}}
                />
            </Form.Item>
            <Form.Item>
                <Row type="flex" justify="end">
                    <Button type="primary" htmlType="submit" loading={formSubmitted}>
                        Move Vehicle
                    </Button>
                </Row>
            </Form.Item>
        </Form>
    );
}

DeploymentCreateForm.propTypes = {
    carID: PropTypes.number.isRequired,
}

export default DeploymentCreateForm;
